import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_DATA_SOURCE, UPDATE_DATA_SOURCE } from '../../graphql';
import type { DataSource } from '../../types';
import { ConnDialog, CxInput, CxSteps, RadioCards } from './ConnDialog';
import { DataSourceConfig } from './DataSourceConfig';

/**
 * Connections → Add a source, Configure, and Connect a WordPress site
 * (Prototype v15 "Connections dialog"). Only the kinds of source the server
 * can take in today are offered: feeds and YouTube channels. Email forwarding
 * and partner feeds in the design aren't built yet, so they aren't offered.
 */

type SourceType = 'rss' | 'mrss' | 'youtube';

const TYPES: Array<{ value: SourceType; title: string; summary: string; ph: string }> = [
  {
    value: 'rss',
    title: 'RSS or Atom feed',
    summary: 'New posts from a news site or blog arrive as sources.',
    ph: 'e.g. Industry news',
  },
  {
    value: 'mrss',
    title: 'Media RSS feed',
    summary: 'A feed of videos or photos, with their media.',
    ph: 'e.g. Video highlights',
  },
  {
    value: 'youtube',
    title: 'YouTube channel',
    summary: 'New videos from a channel arrive as sources.',
    ph: 'e.g. Our channel',
  },
];

const URL_RE = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/\S*)?$/i;
const withScheme = (u: string) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

function urlNote(type: SourceType, bad: boolean) {
  if (type === 'youtube') {
    return bad
      ? 'That doesn’t look like a web address.'
      : 'Paste the channel’s address, like youtube.com/@yourchannel.';
  }
  return bad
    ? 'That doesn’t look like a web address. Feeds often end in /feed or .xml.'
    : 'Paste the feed’s address.';
}

export function AddSourceDialog({
  accountId,
  onClose,
  onAdded,
}: {
  accountId: string;
  onClose: () => void;
  onAdded: (name: string) => void;
}) {
  const [step, setStep] = useState<'pick' | 'set'>('pick');
  const [type, setType] = useState<SourceType | null>(null);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [tried, setTried] = useState(false);
  const [error, setError] = useState('');
  const [create, { loading }] = useMutation(CREATE_DATA_SOURCE);

  if (step === 'pick' || !type) {
    return (
      <ConnDialog
        title="Add a source"
        sub="Where your agent finds material to write from."
        onClose={onClose}
        cta={{ label: 'Next', disabled: !type, onClick: () => type && setStep('set') }}
      >
        <RadioCards options={TYPES} value={type} onChange={setType} />
      </ConnDialog>
    );
  }

  const meta = TYPES.find((t) => t.value === type)!;
  const nameBad = tried && !name.trim();
  const urlBad = tried && !URL_RE.test(url.trim());
  return (
    <ConnDialog
      title={meta.title}
      sub={meta.summary}
      onClose={onClose}
      onBack={() => setStep('pick')}
      cta={{
        label: loading ? 'Adding…' : 'Add source',
        disabled: loading,
        onClick: async () => {
          if (!name.trim() || !URL_RE.test(url.trim())) return setTried(true);
          setError('');
          try {
            await create({
              variables: {
                input: { accountId, name: name.trim(), type, url: withScheme(url.trim()) },
              },
            });
            onAdded(name.trim());
          } catch (err) {
            setError(err instanceof Error ? err.message : 'The source wasn’t added. Try again.');
          }
        },
      }}
    >
      <CxInput
        label="Name"
        value={name}
        onChange={setName}
        placeholder={meta.ph}
        autoFocus
        bad={nameBad}
        note={nameBad ? 'Give it a name.' : undefined}
      />
      <CxInput
        label={type === 'youtube' ? 'Channel address' : 'Feed address'}
        value={url}
        onChange={setUrl}
        placeholder={
          type === 'youtube' ? 'https://www.youtube.com/@channel' : 'https://example.com/feed'
        }
        mono
        maxLength={500}
        bad={urlBad}
        note={urlNote(type, urlBad)}
      />
      {error ? (
        <p className="cx-note" data-bad="true">
          {error}
        </p>
      ) : null}
    </ConnDialog>
  );
}

export function ConfigureSourceDialog({
  accountId,
  source,
  kind,
  onClose,
  onSaved,
}: {
  accountId: string;
  source: DataSource;
  /** "RSS feed", "YouTube"… */
  kind: string;
  onClose: () => void;
  onSaved: (name: string) => void;
}) {
  const [name, setName] = useState(source.name || '');
  const [on, setOn] = useState(source.isActive);
  const [tried, setTried] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [error, setError] = useState('');
  const [update, { loading }] = useMutation(UPDATE_DATA_SOURCE);
  const since = source.createdAt
    ? new Date(source.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  const feed = ['rss', 'mrss'].includes(source.type?.toLowerCase());
  const nameBad = tried && !name.trim();

  return (
    <ConnDialog
      title={source.name || 'Source'}
      sub={`${kind}${since ? ` · since ${since}` : ''}`}
      onClose={onClose}
      cta={{
        label: loading ? 'Saving…' : 'Save',
        disabled: loading,
        onClick: async () => {
          if (!name.trim()) return setTried(true);
          setError('');
          try {
            await update({
              variables: { id: source.id, input: { name: name.trim(), isActive: on } },
            });
            onSaved(name.trim());
          } catch (err) {
            setError(err instanceof Error ? err.message : 'That didn’t save. Try again.');
          }
        },
      }}
    >
      <button
        type="button"
        className="cx-toggle"
        role="switch"
        aria-checked={on}
        onClick={() => setOn((v) => !v)}
      >
        <span className="cx-toggle-text">
          <span className="cx-radio-title">Take in new items</span>
          <span className="cx-radio-summary">
            {on
              ? 'On. New items come in as they’re published.'
              : 'Paused. Nothing new comes in. Saved items stay.'}
          </span>
        </span>
        <span className="ink-switch" data-on={on} />
      </button>
      <CxInput
        label="Name"
        value={name}
        onChange={setName}
        placeholder="Name"
        bad={nameBad}
        note={nameBad ? 'Give it a name.' : undefined}
      />
      {source.url ? (
        <div className="cx-field">
          <span className="cx-label">Address</span>
          <code className="cx-value">{source.url}</code>
        </div>
      ) : null}
      {feed ? (
        <div className="cx-field">
          <button type="button" className="ink-site-link" onClick={() => setMapping((v) => !v)}>
            {mapping ? 'Hide field mapping' : 'Field mapping'}
          </button>
          {mapping ? <DataSourceConfig dataSourceId={source.id} accountId={accountId} /> : null}
        </div>
      ) : null}
      {error ? (
        <p className="cx-note" data-bad="true">
          {error}
        </p>
      ) : null}
    </ConnDialog>
  );
}

/**
 * The plugin pairs from WordPress: its Connect button brings the person here to
 * choose the site and approve it (account/ConnectWordPress.tsx). So this says
 * how, rather than minting a code nobody can type in yet.
 */
export function WordPressDialog({ onClose }: { onClose: () => void }) {
  return (
    <ConnDialog
      title="Connect a WordPress site"
      sub="Bring the posts from a WordPress site you already run into tinycms."
      onClose={onClose}
      cta={{ label: 'Done', onClick: onClose }}
    >
      <CxSteps
        head="IN WORDPRESS"
        steps={[
          { t: 'Install and activate the tinycms plugin.' },
          { t: 'Open the plugin’s settings page and choose Connect.' },
          {
            t: 'You come back here to choose this site and approve it. The WordPress site then shows in this list.',
          },
        ]}
      />
    </ConnDialog>
  );
}
